import { useState, useRef, useEffect } from "react";
import { Globe, ChevronDown, X, Check, Search } from "lucide-react";
import { useServidores } from "@/hooks/useServidores";
import { useIsMobile } from "@/hooks/use-mobile";

interface ServerComboboxProps {
  selected: string[];
  onChange: (servers: string[]) => void;
}

const ServerCombobox = ({ selected, onChange }: ServerComboboxProps) => {
  const { data: servidores } = useServidores();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile && open) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [isMobile, open]);

  const filtered = (servidores || []).filter(s =>
    !search.trim() || s.nome.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (nome: string) => {
    onChange(selected.includes(nome) ? selected.filter((s) => s !== nome) : [...selected, nome]);
  };

  const removeServer = (nome: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter((s) => s !== nome));
  };

  const handleOpen = () => {
    if (open) {
      setOpen(false);
      setSearch("");
      return;
    }
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const listContent = (
    <>
      <div className="px-3 py-2 border-b border-border/40">
        <div className="flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Buscar servidor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-transparent font-pixel text-[8px] sm:text-[7px] text-foreground outline-none placeholder:text-muted-foreground/60"
          />
          {search && (
            <button onClick={() => setSearch("")} className="cursor-pointer">
              <X className="w-3 h-3 text-muted-foreground/60" />
            </button>
          )}
        </div>
      </div>

      {selected.length > 0 && (
        <div className="px-3 py-1.5 border-b border-border/40 flex flex-wrap gap-1">
          {selected.map((s) => (
            <span key={s} className="flex items-center gap-0.5 bg-primary/20 text-primary px-1.5 py-0.5 rounded-sm font-pixel text-[7px] whitespace-nowrap">
              {s}
              <X className="w-2.5 h-2.5 cursor-pointer hover:text-destructive" onClick={(e) => removeServer(s, e)} />
            </span>
          ))}
        </div>
      )}

      <div className={isMobile ? "flex-1 overflow-auto" : "max-h-[200px] overflow-auto"}>
        {filtered.length === 0 ? (
          <div className="px-3 py-4 font-pixel text-[8px] sm:text-[7px] text-muted-foreground text-center">Nenhum servidor</div>
        ) : (
          filtered.map((s) => {
            const active = selected.includes(s.nome);
            return (
              <button
                key={s.id}
                onClick={() => toggle(s.nome)}
                className="w-full flex items-center gap-2 px-3 py-2 sm:py-1.5 font-pixel text-[8px] sm:text-[7px] hover:bg-primary/10 active:bg-primary/20 transition-colors cursor-pointer text-left"
              >
                <div className={`w-4 h-4 sm:w-3 sm:h-3 border rounded-sm flex items-center justify-center ${active ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
                  {active && <Check className="w-2.5 h-2.5 sm:w-2 sm:h-2 text-primary-foreground" />}
                </div>
                <span className={`flex-1 ${active ? "text-primary" : "text-muted-foreground"}`}>{s.nome}</span>
              </button>
            );
          })
        )}
      </div>
    </>
  );

  return (
    <>
      <div ref={ref} className="relative flex flex-col gap-0.5">
        <label className="font-pixel text-[6px] text-muted-foreground flex items-center gap-1">
          <Globe className="w-2.5 h-2.5" /> Servidor
        </label>
        <button
          onClick={handleOpen}
          className="flex items-center gap-1 px-2 py-1.5 tibia-border bg-card rounded-sm font-pixel text-[7px] text-muted-foreground cursor-pointer min-w-[120px] max-w-[200px]"
        >
          <div className="flex-1 flex items-center gap-1 overflow-hidden">
            {selected.length === 0 ? (
              <span className="text-muted-foreground/60">Todos</span>
            ) : (
              <div className="flex items-center gap-0.5 overflow-hidden">
                {selected.slice(0, 2).map((s) => (
                  <span key={s} className="flex items-center gap-0.5 bg-primary/20 text-primary px-1 rounded-sm text-[6px] whitespace-nowrap">
                    {s}
                    <X className="w-2 h-2 cursor-pointer hover:text-destructive" onClick={(e) => removeServer(s, e)} />
                  </span>
                ))}
                {selected.length > 2 && (
                  <span className="text-[6px] text-primary">+{selected.length - 2}</span>
                )}
              </div>
            )}
          </div>
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && !isMobile && (
          <div className="absolute top-full left-0 mt-1 z-50 tibia-border bg-card rounded-sm py-1 min-w-[150px] max-h-[200px] overflow-auto shadow-lg">
            {(servidores || []).map((s) => {
              const active = selected.includes(s.nome);
              return (
                <button
                  key={s.id}
                  onClick={() => toggle(s.nome)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 font-pixel text-[7px] hover:bg-primary/10 transition-colors cursor-pointer text-left"
                >
                  <div className={`w-3 h-3 border rounded-sm flex items-center justify-center ${active ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
                    {active && <Check className="w-2 h-2 text-primary-foreground" />}
                  </div>
                  <span className={`flex-1 ${active ? "text-primary" : "text-muted-foreground"}`}>{s.nome}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {open && isMobile && (
        <div className="fixed inset-0 z-[200] bg-background flex flex-col">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-border/40">
            <span className="font-pixel text-[9px] text-foreground">Selecionar Servidor</span>
            <button onClick={() => { setOpen(false); setSearch(""); }} className="font-pixel text-[8px] text-primary cursor-pointer px-2 py-1">
              Fechar
            </button>
          </div>
          {listContent}
        </div>
      )}
    </>
  );
};

export default ServerCombobox;
